import cornerstone from 'cornerstone-core';
import domtoimage from 'dom-to-image';
import { getVolumeManager } from '../components/App/Viewer/getVolumeManager';
import store from '../store/store';
import { checkClipboardPermission } from './clipboardUtils';

const writeToClipboard = async (blob) => {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob
      })
    ]);
  } catch (err) {
    console.error('Failed to write to clipboard', err);
  }
};

const downloadBlob = (blob, filename) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename || `capture-${new Date().toISOString()}.png`;
  link.click();
  URL.revokeObjectURL(url);
};

const showNotSecureAlert = (t) => {
  const notSecure = typeof t === 'function'
    ? t('tooltip.notSecure')
    : 'Clipboard access requires a secure connection.';
  alert(notSecure);
};

export const captureScreenShot = async () => {
  const state = store.getState((state) => state);
  const { layout } = state;
  const cornerstoneElements = cornerstone.getEnabledElements();
  const currentCornerstoneElement = cornerstoneElements[layout.selectedViewIndex];

  return domtoimage.toBlob(currentCornerstoneElement.element);
};

export const screenShot = async (t) => {
  const hasPermission = await checkClipboardPermission();
  if (!hasPermission) {
    return showNotSecureAlert(t);
  }

  try {
    const blob = await captureScreenShot();
    await writeToClipboard(blob);
  } catch (err) {
    console.error('Failed to capture screenshot', err);
  }
};

export const downloadScreenShot = async (blob, filename) => {
  try {
    downloadBlob(blob || await captureScreenShot(), filename);
  } catch (err) {
    console.error('Failed to download screenshot', err);
  }
};

const drawViewportCrop = (context, source, viewportBounds, width, height, sourceBoundsOverride) => {
  if (!source) return;
  const sourceBounds = sourceBoundsOverride || source.getBoundingClientRect();
  if (!sourceBounds.width || !sourceBounds.height) return;

  const scaleX = source.width / sourceBounds.width;
  const scaleY = source.height / sourceBounds.height;
  const sourceX = (viewportBounds.left - sourceBounds.left) * scaleX;
  const sourceY = (viewportBounds.top - sourceBounds.top) * scaleY;
  const sourceWidth = viewportBounds.width * scaleX;
  const sourceHeight = viewportBounds.height * scaleY;
  context.drawImage(
    source,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    width,
    height
  );
};

const loadImage = (source) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = source;
});

export const captureScreenShot3D = async () => {
  const state = store.getState((state) => state);
  const { layout } = state;
  const { selectedViewIndex } = layout;

  const volumeManager3d = getVolumeManager();
  try {
    const views = volumeManager3d?.all_views || [];
    const selectedView = views.find(
      (view) => Number(view.div?.dataset?.viewIndex) === selectedViewIndex
    );
    const hasIndexedViews = views.some((view) => view.div?.dataset?.viewIndex !== undefined);
    const selectedDiv = selectedView?.div || (hasIndexedViews ? null : views[selectedViewIndex]?.div);
    if (!selectedDiv) return null;

    const viewportBounds = selectedDiv.getBoundingClientRect();
    const width = Math.round(viewportBounds.width);
    const height = Math.round(viewportBounds.height);
    if (!width || !height) return null;

    const capture = document.createElement('canvas');
    capture.width = width;
    capture.height = height;
    const context = capture.getContext('2d');
    if (!context) return null;

    [
      volumeManager3d.canvas,
      volumeManager3d.canvas2d,
      volumeManager3d.canvasOverlayBottom,
      volumeManager3d.canvasOverlayTop,
    ].forEach((source) => drawViewportCrop(context, source, viewportBounds, width, height));

    // The WebGL and overlay canvases are siblings of the viewport div. Add the
    // viewport's DOM overlays separately after copying those canvas layers.
    try {
      const overlayDataUrl = await domtoimage.toPng(selectedDiv);
      const overlay = await loadImage(overlayDataUrl);
      context.drawImage(overlay, 0, 0, width, height);
    } catch (overlayError) {
      // The WebGL image is still useful if an HTML overlay cannot be serialized.
      console.warn('Unable to capture 3D viewport HTML overlays', overlayError);
    }

    if (volumeManager3d.svgElem) {
      try {
        const svgDataUrl = await domtoimage.toPng(volumeManager3d.svgElem);
        const svgOverlay = await loadImage(svgDataUrl);
        drawViewportCrop(
          context,
          svgOverlay,
          viewportBounds,
          width,
          height,
          volumeManager3d.svgElem.getBoundingClientRect()
        );
      } catch (overlayError) {
        console.warn('Unable to capture 3D viewport SVG overlays', overlayError);
      }
    }

    return new Promise((resolve) => capture.toBlob(resolve, 'image/png'));
  } catch (err) {
    console.error('Failed to capture 3D screenshot', err);
    return null;
  }
};

export const screenShot3D = async (t) => {
  const hasPermission = await checkClipboardPermission();
  if (!hasPermission) {
    return showNotSecureAlert(t);
  }

  const blob = await captureScreenShot3D();
  if (blob) await writeToClipboard(blob);
};

export const downloadScreenShot3D = async (captureBlob, filename) => {
  const blob = captureBlob || await captureScreenShot3D();
  if (blob) downloadBlob(blob, filename);
};

export const captureScreenShotECG = async (viewIndex) => {
  const state = store.getState();
  const selectedViewIndex = Number.isInteger(viewIndex)
    ? viewIndex
    : state.layout.selectedViewIndex;
  const waveformViewport = document.querySelector(
    `[data-waveform-view-index="${selectedViewIndex}"]`
  );
  if (!waveformViewport) return null;

  try {
    // Capture the whole ECG viewport so the export includes every displayed
    // lead and the study/patient information above the traces.
    return await domtoimage.toBlob(waveformViewport, { bgcolor: '#101315' });
  } catch (error) {
    console.error('Failed to capture ECG screenshot', error);
    return null;
  }
};

export const screenShotECG = async (t, viewIndex) => {
  const hasPermission = await checkClipboardPermission();
  if (!hasPermission) {
    return showNotSecureAlert();
  }

  try {
    const blob = await captureScreenShotECG(viewIndex);
    await writeToClipboard(blob);
  } catch (err) {
    console.error('Failed to capture ECG screenshot', err);
  }
};

export const downloadScreenShotECG = async (blob, filename) => {
  try {
    downloadBlob(blob || await captureScreenShotECG(), filename);
  } catch (err) {
    console.error('Failed to download ECG screenshot', err);
  }
};
